import React, { useEffect, useMemo, useRef, useState } from "react";
import PortalLayout from "../components/PortalLayout";
import AngelCard from "../components/AngelCard";
import AngelSparks from "../components/AngelSparks";
import WhatsNewBanner from "../components/WhatsNewBanner";
import SquadVoice from "../components/SquadVoice";
import { api } from "../lib/api";
import { saveOrShareBlob } from "../lib/share";
import { useMember } from "../context/MemberContext";
import { formatCooldownDate, isCooldownActive } from "../lib/cooldown";
import { Search, X, Lock, HelpCircle, ShoppingBag, ArrowUpRight, Sparkles, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function AngelsGrid() {
    const { member } = useMember();
    const [angels, setAngels] = useState([]);
    const [selectedNumber, setSelectedNumber] = useState(null);
    const [nextChangeAllowedAt, setNextChangeAllowedAt] = useState(null);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState("");
    const [availableOnly, setAvailableOnly] = useState(false);
    const [waitingOnly, setWaitingOnly] = useState(false);
    // Set of angel numbers (strings) picked by other members in the last hour.
    // Drives the soft heartbeat pulse on each AngelCard.
    const [recentlyChosen, setRecentlyChosen] = useState(() => new Set());
    // Total number of picks across all angels in the same hour window —
    // surfaced as a tiny "X picks this hour" live counter above the grid.
    const [recentPicksTotal, setRecentPicksTotal] = useState(0);
    // Cumulative "this week so far" stats for the hero strapline. Quiet
    // social proof — visible to all members, no exclusions.
    const [weekTotals, setWeekTotals] = useState(null); // {membersLast7d, picksLast7d}
    // One-time golden sparks when a squad milestone landed this week.
    const [celebrateSparks, setCelebrateSparks] = useState(false);
    const [downloadingCard, setDownloadingCard] = useState(false);
    const selectedCardRef = useRef(null);

    const milestoneMoment = useMemo(() => {
        const m = weekTotals?.milestone;
        if (!m?.celebrated || !m.reachedAt) return null;
        if (Date.now() - new Date(m.reachedAt).getTime() > 7 * 24 * 3600 * 1000) return null;
        return m;
    }, [weekTotals]);

    useEffect(() => {
        if (!milestoneMoment) return;
        const key = `angelsquad.milestoneCelebrated.${milestoneMoment.count}`;
        try {
            if (window.localStorage.getItem(key)) return;
            window.localStorage.setItem(key, "1");
        } catch { /* ignore */ }
        setCelebrateSparks(true);
    }, [milestoneMoment]);

    const downloadCelebrationCard = async () => {
        if (!milestoneMoment || downloadingCard) return;
        setDownloadingCard(true);
        try {
            const res = await api.get("/milestone/card", { responseType: "blob" });
            const result = await saveOrShareBlob({
                blob: res.data,
                filename: `angel-squad-we-are-${milestoneMoment.count}.png`,
                mimeType: "image/png",
                title: `We are ${milestoneMoment.count}.`,
                text: "The Angel Squad is complete.",
            });
            if (result?.cancelled) toast(`Celebration card — cancelled.`);
            else if (result?.shared) toast.success("Celebration card shared.");
            else toast.success("Celebration card saved — post it proudly.");
        } catch {
            toast.error("Could not build the celebration card — please try again.");
        } finally {
            setDownloadingCard(false);
        }
    };

    useEffect(() => {
        const load = async () => {
            try {
                const [angelsRes, selectionRes] = await Promise.all([
                    api.get("/angels"),
                    api.get("/selections/me"),
                ]);
                setAngels(angelsRes.data);
                setSelectedNumber(selectionRes.data?.selection?.angelNumber || null);
                setNextChangeAllowedAt(selectionRes.data?.nextChangeAllowedAt || null);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, []);

    // Live "recently chosen" feed — polled every 60s while the grid is open.
    // Failures are silent; the pulse simply doesn't fire.
    useEffect(() => {
        let cancelled = false;
        const refresh = async () => {
            try {
                const { data } = await api.get("/angels-recent-picks", { params: { within_seconds: 3600 } });
                if (cancelled) return;
                const items = data?.items || [];
                const next = new Set(items.map((i) => i.angelNumber));
                setRecentlyChosen(next);
                setRecentPicksTotal(items.reduce((sum, i) => sum + (i.count || 0), 0));
            } catch {
                // ignore — keep prior set
            }
        };
        refresh();
        const id = window.setInterval(refresh, 60_000);
        return () => {
            cancelled = true;
            window.clearInterval(id);
        };
    }, []);

    // Cumulative "this week so far" totals — fetched once on mount.
    // Best-effort; if the endpoint errors we simply omit the strapline.
    useEffect(() => {
        let cancelled = false;
        api.get("/squad-totals")
            .then(({ data }) => { if (!cancelled) setWeekTotals(data); })
            .catch(() => { /* silent */ });
        return () => { cancelled = true; };
    }, []);

    // Once the grid has loaded and we know the chosen Angel, scroll it into
    // view so a returning member sees their card without hunting for it.
    useEffect(() => {
        if (!loading && selectedNumber && selectedCardRef.current) {
            const t = setTimeout(() => {
                selectedCardRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
            }, 350); // let card-enter animations settle first
            return () => clearTimeout(t);
        }
    }, [loading, selectedNumber]);

    const selectableCount = angels.filter((a) => a.isSelectable).length;
    const firstName = (member && member.fullName) ? member.fullName.split(" ")[0] : null;
    const isReturning = !!selectedNumber;

    const filteredAngels = useMemo(() => {
        const q = query.trim().toLowerCase();
        return angels.filter((a) => {
            if (availableOnly && !a.isSelectable) return false;
            if (waitingOnly && !a.waiting) return false;
            if (!q) return true;
            // Match the angel number digits ("7" or "007") or the displayed numeral string.
            const padded = String(a.number).padStart(3, "0");
            const unpadded = String(parseInt(a.number, 10) || a.number);
            if (padded.includes(q) || unpadded.includes(q)) return true;
            // Also match approved attribute words ("strength" → Angel 008),
            // so members can search by feeling, not just by number.
            return (a.approvedAttributes || []).some((t) => t.toLowerCase().includes(q));
        });
    }, [angels, query, availableOnly, waitingOnly]);

    const filtersActive = query.trim().length > 0 || availableOnly || waitingOnly;
    const waitingCount = useMemo(() => angels.filter((a) => a.waiting).length, [angels]);

    // Angels opened for selection within the last 14 days (sketch uploads).
    const newArrivals = useMemo(() => angels.filter(
        (a) => a.openedForSelectionAt
            && Date.now() - new Date(a.openedForSelectionAt).getTime() < 14 * 24 * 3600 * 1000
    ), [angels]);
    const clearFilters = () => {
        setQuery("");
        setAvailableOnly(false);
        setWaitingOnly(false);
    };

    return (
        <PortalLayout>
            <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 pt-20 pb-32" data-testid="angels-grid-page">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-6">
                    <div>
                        <span className="font-label text-[11px] text-[#cfa052]">
                            {isReturning ? "Welcome back" : "The Sixty"}
                        </span>
                        <h1 className="mt-4 font-serif-display text-5xl md:text-6xl tracking-tighter text-[#e8e4db]" data-testid="grid-title">
                            {firstName ? (
                                <>
                                    {isReturning ? "Welcome back, " : "Welcome, "}
                                    <span className="italic text-[#cfa052]">{firstName}</span>
                                    {" — "}
                                    {isReturning ? "your Angel awaits." : "choose your Angel."}
                                </>
                            ) : (
                                <>Choose your <span className="italic">Angel</span></>
                            )}
                        </h1>
                        <div className="mt-6">
                            <a
                                href="https://dalboocreate.com"
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => {
                                    // Fire-and-forget outbound-click attribution.
                                    api.post("/track/outbound", { dest: "dalboocreate" }).catch(() => {});
                                }}
                                data-testid="grid-hero-merch-cta"
                                className="inline-flex items-center gap-2.5 bg-[#cfa052] hover:bg-[#e0b569] text-[#0c0b0a] px-6 py-3 rounded-full font-label text-[11px] tracking-[0.25em] uppercase transition-colors duration-300 shadow-[0_0_30px_rgba(207,160,82,0.25)]"
                            >
                                <ShoppingBag className="w-3.5 h-3.5" aria-hidden="true" />
                                Shop Angelwear at dalboocreate.com
                                <ArrowUpRight className="w-3.5 h-3.5" aria-hidden="true" />
                            </a>
                            <div
                                className="mt-4 flex flex-wrap items-center gap-2.5"
                                data-testid="grid-merch-category-links"
                            >
                                {[
                                    {
                                        label: "Angel pendant necklaces",
                                        href: "https://www.dalboocreate.com/category/angel-pendant-necklaces",
                                        dest: "dalboocreate_pendants",
                                        testid: "grid-merch-link-pendants",
                                    },
                                    {
                                        label: "Angel t-shirts",
                                        href: "https://www.dalboocreate.com/category/tee-shirts-hoodies-sweatshirts",
                                        dest: "dalboocreate_tees",
                                        testid: "grid-merch-link-tees",
                                    },
                                    {
                                        label: "Faith-based streetwear",
                                        href: "https://www.dalboocreate.com/category/faith-based-streetwear",
                                        dest: "dalboocreate_streetwear",
                                        testid: "grid-merch-link-streetwear",
                                    },
                                ].map((cat) => (
                                    <a
                                        key={cat.dest}
                                        href={cat.href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={() => {
                                            // Fire-and-forget outbound-click attribution.
                                            api.post("/track/outbound", { dest: cat.dest }).catch(() => {});
                                        }}
                                        data-testid={cat.testid}
                                        className="inline-flex items-center gap-1.5 border border-[#cfa052]/40 hover:border-[#cfa052] text-[#cfa052] hover:text-[#e0b569] px-4 py-2 rounded-full font-label text-[10px] tracking-[0.2em] uppercase transition-colors duration-300"
                                    >
                                        {cat.label}
                                        <ArrowUpRight className="w-3 h-3" aria-hidden="true" />
                                    </a>
                                ))}
                            </div>
                        </div>
                        {selectedNumber && nextChangeAllowedAt && isCooldownActive(nextChangeAllowedAt) && (
                            <div
                                className="mt-5 inline-flex items-center gap-2.5 bg-[#1a1714]/60 border border-[#cfa052]/30 rounded-sm px-3.5 py-2"
                                data-testid="grid-cooldown-lock"
                            >
                                <Lock className="w-3 h-3 text-[#cfa052]" aria-hidden="true" />
                                <span className="font-poetic text-[12px] text-[#9a958a]">
                                    Your Angel is sealed until{" "}
                                    <span className="text-[#e8e4db]">{formatCooldownDate(nextChangeAllowedAt)}</span>
                                </span>
                                <button
                                    type="button"
                                    onClick={() =>
                                        toast("Why is my Angel sealed?", {
                                            description: `Each member walks with one Angel for seven days at a time, so the connection has room to settle. After ${formatCooldownDate(nextChangeAllowedAt)}, you'll be free to choose again.`,
                                            duration: 7000,
                                        })
                                    }
                                    aria-label="Why is my Angel sealed?"
                                    data-testid="grid-cooldown-help"
                                    className="text-[#6e6a62] hover:text-[#cfa052] transition-colors"
                                >
                                    <HelpCircle className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        )}
                        {weekTotals && weekTotals.membersLast7d > 0 && (
                            <p
                                className="mt-4 font-poetic text-[13px] text-[#9a958a]"
                                data-testid="grid-week-totals"
                            >
                                This week so far —{" "}
                                <span className="text-[#cfa052]">
                                    {weekTotals.membersLast7d} {weekTotals.membersLast7d === 1 ? "squad member" : "squad members"}
                                </span>{" "}
                                {weekTotals.membersLast7d === 1 ? "has chosen." : "have chosen."}
                            </p>
                        )}
                        {weekTotals?.milestone && !weekTotals.milestone.celebrated
                            && weekTotals.milestone.remaining > 0 && weekTotals.milestone.remaining <= 10 && (
                            <div className="mt-3 max-w-xs" data-testid="grid-milestone-countdown">
                                <p className="font-poetic text-[13px] text-[#cfa052]">
                                    {weekTotals.milestone.remaining === 1
                                        ? "One seat left until the squad is complete."
                                        : `${weekTotals.milestone.remaining} seats until the squad is complete.`}
                                </p>
                                <div
                                    className="mt-2 h-1 bg-white/10 rounded-full overflow-hidden"
                                    role="progressbar"
                                    aria-valuenow={weekTotals.milestone.count - weekTotals.milestone.remaining}
                                    aria-valuemin={0}
                                    aria-valuemax={weekTotals.milestone.count}
                                    data-testid="grid-milestone-progress"
                                >
                                    <div
                                        className="h-full bg-[#cfa052] rounded-full transition-[width] duration-1000"
                                        style={{
                                            width: `${Math.min(100, Math.round(((weekTotals.milestone.count - weekTotals.milestone.remaining) / weekTotals.milestone.count) * 100))}%`,
                                        }}
                                    />
                                </div>
                            </div>
                        )}
                        <SquadVoice />
                    </div>
                    <div className="font-poetic text-[#9a958a] max-w-md text-right md:text-left">
                        <p className="text-sm">
                            {selectableCount} ready · {60 - selectableCount} still arriving
                        </p>
                        {selectedNumber && (
                            <p className="text-sm mt-1">
                                Your Angel: <span className="text-[#cfa052] not-italic font-numerals text-lg">{selectedNumber}</span>
                            </p>
                        )}
                    </div>
                </div>

                {celebrateSparks && (
                    <AngelSparks sound={false} onDone={() => setCelebrateSparks(false)} />
                )}
                {/* Squad-complete celebration — golden note for the week the milestone lands */}
                {milestoneMoment && (
                    <div
                        className="mb-10 flex flex-col sm:flex-row sm:items-center gap-4 border border-[#cfa052]/60 bg-[#cfa052]/[0.1] rounded-sm px-6 py-4"
                        data-testid="grid-milestone-celebration"
                    >
                        <Sparkles className="w-5 h-5 text-[#cfa052] shrink-0" aria-hidden="true" />
                        <p className="font-poetic text-[#e8e4db] text-sm md:text-base leading-relaxed flex-1">
                            <span className="font-label text-[10px] tracking-[0.25em] uppercase text-[#cfa052] mr-3">The squad is complete</span>
                            All {milestoneMoment.count} of us are here. Every Angel now walks with someone — thank you for being one of the {milestoneMoment.count}.
                        </p>
                        <button
                            type="button"
                            onClick={downloadCelebrationCard}
                            disabled={downloadingCard}
                            className="inline-flex items-center gap-2 shrink-0 bg-[#cfa052] text-[#0c0b0a] hover:bg-[#e0b569] font-label text-[10px] tracking-[0.25em] uppercase font-semibold px-4 py-2.5 rounded transition-colors disabled:opacity-50"
                            data-testid="grid-milestone-card-btn"
                        >
                            {downloadingCard ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                            Get the card
                        </button>
                    </div>
                )}

                {/* New-arrivals banner — shows for 14 days after angels open for selection */}
                {!loading && newArrivals.length > 0 && (
                    <div
                        className="mb-10 flex items-center gap-4 border border-[#cfa052]/40 bg-[#cfa052]/[0.06] rounded-sm px-6 py-4"
                        data-testid="new-arrivals-banner"
                    >
                        <Sparkles className="w-5 h-5 text-[#cfa052] shrink-0" aria-hidden="true" />
                        <p className="font-poetic text-[#e8e4db] text-sm md:text-base leading-relaxed">
                            <span className="font-label text-[10px] tracking-[0.25em] uppercase text-[#cfa052] mr-3">New arrivals</span>
                            {newArrivals.length >= 9
                                ? "The final nine have landed — all sixty Angels are now here. Come meet the newcomers."
                                : `${newArrivals.length} new ${newArrivals.length === 1 ? "Angel has" : "Angels have"} landed — come meet ${newArrivals.length === 1 ? "the newcomer" : "the newcomers"}: ${newArrivals.map((a) => a.number).join(", ")}.`}
                        </p>
                    </div>
                )}

                {/* First-time hint, only shown when the member has not yet picked */}
                {!loading && !isReturning && (
                    <p
                        className="font-poetic text-[#9a958a] text-base md:text-lg max-w-2xl mb-12 leading-relaxed"
                        data-testid="grid-first-time-hint"
                    >
                        Each card is one of the sixty. Take your time — open any Angel to see them larger, then confirm your choice. You&apos;ll walk with this one Angel through the rest of the squad.
                    </p>
                )}
                {!loading && isReturning && (
                    <p
                        className="font-poetic text-[#9a958a] text-base md:text-lg max-w-2xl mb-12 leading-relaxed"
                        data-testid="grid-returning-hint"
                    >
                        Your chosen Angel is highlighted below in gold. Open any card to sit with it again, or visit your selection page to download your certificate.
                    </p>
                )}

                {loading ? (
                    <div className="text-center py-32 font-poetic text-[#9a958a]" data-testid="grid-loading">gathering the squad…</div>
                ) : (
                    <>
                        <WhatsNewBanner />

                        {/* Search + availability filter */}
                        <div
                            className="mb-10 flex flex-col sm:flex-row sm:items-center gap-4"
                            data-testid="grid-filters"
                        >
                            <div className="relative flex-1 max-w-xs">
                                <Search
                                    className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6e6a62] pointer-events-none"
                                    size={14}
                                    aria-hidden="true"
                                />
                                <input
                                    type="search"
                                    placeholder="Search by number or attribute"
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    aria-label="Search angels by number or attribute"
                                    data-testid="grid-search-input"
                                    className="w-full bg-[#151311] border border-white/10 focus:border-[#cfa052]/60 focus:outline-none text-[#e8e4db] placeholder:text-[#6e6a62] text-sm font-poetic rounded-sm pl-9 pr-9 py-2.5 transition-colors"
                                />
                                {query && (
                                    <button
                                        type="button"
                                        onClick={() => setQuery("")}
                                        aria-label="Clear search"
                                        data-testid="grid-search-clear"
                                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[#9a958a] hover:text-[#e8e4db] transition-colors"
                                    >
                                        <X size={12} />
                                    </button>
                                )}
                            </div>
                            <label
                                className="inline-flex items-center gap-2 cursor-pointer select-none"
                                data-testid="grid-available-toggle"
                            >
                                <input
                                    type="checkbox"
                                    checked={availableOnly}
                                    onChange={(e) => setAvailableOnly(e.target.checked)}
                                    data-testid="grid-available-checkbox"
                                    className="h-4 w-4 accent-[#cfa052] cursor-pointer"
                                />
                                <span className="font-label text-[10px] tracking-[0.25em] uppercase text-[#9a958a]">
                                    Available only
                                </span>
                            </label>
                            {!selectedNumber && (
                                <label
                                    className="inline-flex items-center gap-2 cursor-pointer select-none"
                                    data-testid="grid-waiting-toggle"
                                >
                                    <input
                                        type="checkbox"
                                        checked={waitingOnly}
                                        onChange={(e) => setWaitingOnly(e.target.checked)}
                                        data-testid="grid-waiting-checkbox"
                                        className="h-4 w-4 accent-[#cfa052] cursor-pointer"
                                    />
                                    <span className="font-label text-[10px] tracking-[0.25em] uppercase text-[#cfa052]/80">
                                        Still waiting
                                    </span>
                                </label>
                            )}
                            {!selectedNumber && waitingCount > 0 && (
                                <span className="font-poetic text-xs text-[#6e6a62]" data-testid="grid-waiting-count">
                                    {waitingCount} Angel{waitingCount === 1 ? "" : "s"} still waiting
                                </span>
                            )}
                            {filtersActive && (
                                <button
                                    type="button"
                                    onClick={clearFilters}
                                    data-testid="grid-clear-filters"
                                    className="font-label text-[10px] tracking-[0.25em] uppercase text-[#cfa052] hover:text-[#e0b569] transition-colors"
                                >
                                    Clear
                                </button>
                            )}
                            <span
                                className="ml-auto flex items-center gap-5 font-poetic text-xs text-[#6e6a62]"
                                data-testid="grid-filter-count-row"
                            >
                                {recentPicksTotal > 0 && (
                                    <span
                                        className="inline-flex items-center gap-2"
                                        data-testid="grid-live-pulse-count"
                                        title="Squad picks in the last hour"
                                    >
                                        <span
                                            aria-hidden="true"
                                            className="relative flex items-center justify-center w-1.5 h-1.5"
                                        >
                                            <span className="absolute inset-0 rounded-full bg-[#cfa052] opacity-60 animate-ping" />
                                            <span className="relative w-1.5 h-1.5 rounded-full bg-[#cfa052]" />
                                        </span>
                                        <span className="font-label text-[10px] tracking-[0.25em] uppercase text-[#cfa052]">
                                            {recentPicksTotal} {recentPicksTotal === 1 ? "pick" : "picks"} this hour
                                        </span>
                                    </span>
                                )}
                                <span data-testid="grid-filter-count">
                                    {filteredAngels.length} of {angels.length} shown
                                </span>
                            </span>
                        </div>

                        <div
                            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6"
                            data-testid="angels-grid"
                        >
                            {filteredAngels.map((angel, idx) => {
                                const isMine = selectedNumber && selectedNumber === angel.number;
                                return (
                                    <div
                                        key={angel.number}
                                        ref={isMine ? selectedCardRef : null}
                                        data-mine={isMine ? "true" : undefined}
                                    >
                                        <AngelCard
                                            angel={angel}
                                            selectedNumber={selectedNumber}
                                            index={idx}
                                            recentlyChosen={recentlyChosen.has(angel.number)}
                                            showWaitingNote={!selectedNumber}
                                        />
                                    </div>
                                );
                            })}
                        </div>

                        {filteredAngels.length === 0 && (
                            <div
                                className="mt-12 font-poetic text-[#9a958a] text-center"
                                data-testid="grid-empty-state"
                            >
                                No Angels match those filters.{" "}
                                <button
                                    type="button"
                                    onClick={clearFilters}
                                    className="text-[#cfa052] hover:text-[#e0b569] underline underline-offset-4 transition-colors"
                                    data-testid="grid-empty-clear"
                                >
                                    Clear filters
                                </button>
                            </div>
                        )}

                        <div
                            className="mt-24 pt-12 border-t border-white/5 max-w-3xl"
                            data-testid="grid-about-art"
                        >
                            <span className="font-label text-[11px] tracking-[0.25em] text-[#cfa052] uppercase">
                                About the art
                            </span>
                            <p
                                className="mt-4 font-poetic text-[#9a958a] text-base md:text-lg leading-relaxed"
                                data-testid="grid-about-art-body"
                            >
                                All Angels are first captured in an initial sketch through an automatic drawing
                                process, then redesigned to be printed in various forms on t-shirts, pendants, and
                                other things Angel. You can check out Angelwear at{" "}
                                <a
                                    href="https://dalboocreate.com"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={() => {
                                        // Fire-and-forget outbound-click attribution.
                                        // Errors are silent — never block the navigation.
                                        api.post("/track/outbound", { dest: "dalboocreate" }).catch(() => {});
                                    }}
                                    className="text-[#cfa052] hover:text-[#e8e4db] underline underline-offset-4 decoration-[#cfa052]/40 transition-colors"
                                    data-testid="grid-about-art-link"
                                >
                                    dalboocreate.com
                                </a>
                                .
                            </p>
                        </div>
                    </>
                )}
            </section>
        </PortalLayout>
    );
}
